import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Mail, PenLine, Send } from "lucide-react";
import { Section } from "./builderUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mra_jwt");
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const money = (c) => `$${Math.round((c || 0) / 100).toLocaleString()}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

const NoteForm = ({ s, studentFirst, onSent, onCancel }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await client.post(`/campaigns/${s.campaignId}/supporters/${s.supporter_key}/thank-you`, { message: text.trim() });
      toast.success(`Sent to ${s.first_name}.`, { description: "They'll get it by email in a minute or two." });
      onSent(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-md border border-border bg-bone p-4" data-testid={`supporter-note-form-${s.supporter_key}`}>
      <p className="text-[13px] text-olive">
        Email opens with <span className="font-semibold text-ink">"{s.first_name}, {studentFirst} wrote you a note."</span> then your words, exactly as written.
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={3} autoFocus
        placeholder={`Thank you, ${s.first_name}. Because of you…`} data-testid={`supporter-note-input-${s.supporter_key}`}
        className="mt-3 w-full rounded-md border border-border bg-sand px-4 py-3 text-base leading-relaxed text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[13px] text-olive">{text.length}/600</span>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onCancel} className="text-[13px] font-semibold text-olive hover:text-ink">Cancel</button>
          <button type="button" onClick={send} disabled={busy || text.trim().length < 2} data-testid={`supporter-note-send-${s.supporter_key}`}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send to {s.first_name}
          </button>
        </div>
      </div>
      {err && <p role="alert" className="mt-2 text-[13px] text-scarlet" data-testid={`supporter-note-error-${s.supporter_key}`}>{err}</p>}
    </div>
  );
};

const Row = ({ s, studentFirst, open, onOpen, onClose, onSent }) => (
  <li data-testid={`supporter-row-${s.supporter_key}`} data-thanked={s.last_thanked_at ? "true" : "false"} className="border-t border-border/70 py-4 first:border-t-0">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-serif text-3xl leading-tight text-ink">{s.display_name}</p>
        <p className="mt-0.5 text-[13px] text-olive">
          {money(s.total_cents)} · {s.gift_count === 1 ? "one gift" : `${s.gift_count} gifts`} · last {day(s.last_gift_at)}
          {!s.can_thank && " · gave without an email, so a note can't reach them"}
        </p>
        {s.last_thanked_at && (
          <p className="mt-2 flex items-start gap-2 text-[13px] text-olive" data-testid={`supporter-last-note-${s.supporter_key}`}>
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grass" strokeWidth={2.4} />
            <span>Thanked {day(s.last_thanked_at)}: <em className="italic">“{s.last_note}”</em></span>
          </p>
        )}
      </div>
      {s.can_thank && !open && (
        <button type="button" onClick={onOpen} data-testid={`supporter-thank-btn-${s.supporter_key}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${s.last_thanked_at ? "border border-border bg-bone text-ink hover:border-ink" : "bg-ink text-sand hover:bg-forest"}`}>
          <PenLine className="h-3.5 w-3.5" /> {s.last_thanked_at ? "Write again" : "Write a thank-you"}
        </button>
      )}
    </div>
    {open && <NoteForm s={s} studentFirst={studentFirst} onSent={onSent} onCancel={onClose} />}
  </li>
);

/**
 * SupporterThanks — students write a personal note to each person who gave
 * with their campaign in mind; we email it (once per supporter per day).
 */
export const SupporterThanks = ({ campaignId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { setData((await client.get(`/campaigns/${campaignId}/supporters`)).data); } catch { setData({ supporters: [], student_first_name: "" }); }
  }, [campaignId]);
  useEffect(() => { if (campaignId) load(); }, [campaignId, load]);

  if (!data || data.supporters.length === 0) return null;
  const thanked = data.supporters.filter((s) => s.last_thanked_at).length;
  return (
    <Section id="supporters" title="Thank the people who gave" accent="bg-grass" testid="supporter-thanks"
      subtitle={`${data.supporters.length} ${data.supporters.length === 1 ? "person has" : "people have"} given with you in mind. A short personal note means more than you'd think; we email it to them for you.`}
      badge={<span className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.2em] text-olive"><Mail className="h-3.5 w-3.5" /> {thanked}/{data.supporters.length} thanked</span>}>
      <ul data-testid="supporter-list">
        {data.supporters.map((s) => (
          <Row key={s.supporter_key} s={{ ...s, campaignId }} studentFirst={data.student_first_name}
            open={open === s.supporter_key} onOpen={() => setOpen(s.supporter_key)} onClose={() => setOpen(null)}
            onSent={(row) => { setOpen(null); setData((d) => ({ ...d, supporters: d.supporters.map((x) => (x.supporter_key === row.supporter_key ? row : x)) })); }} />
        ))}
      </ul>
      <p className="mt-4 text-[13px] leading-relaxed text-olive">
        Your supporters' emails stay private; we deliver the note for you. Names shown are how donors entered them at checkout.
      </p>
    </Section>
  );
};
